import { useModuleBEM } from "@b2bportal/core-themes";
import type { GroundCancelScenarioV2 } from "@b2bportal/rbc-ground-cancel-v2-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Button, ButtonTypeEnum, Card } from "@tenant/ui";
import DOMPurify from "dompurify";
import defaultStyles from "./CarCancellationSupport.module.scss";
import { ContactSupportModal } from "@/components";
import { getCancelCopy } from "@/modules/cars-postbooking/components/CarTripCancellationPolicy/getCancelCopy";
import { CancelImportantInfo } from "@/pages/Trips/shared/components";

export interface CarCancellationSupportProps {
  cancelScenario: GroundCancelScenarioV2 | null | undefined;
  onBack: () => void;
}

const ComponentName = "CarCancellationSupport";

/**
 * Rendered when the cancellation quote loaded but the booking cannot be
 * cancelled through self-serve (contact customer service, already cancelled,
 * booking pending, pickup in the past). Shows the scenario copy from the
 * backend with a contact-support CTA. Deliberately has no "Try again" button:
 * there is nothing to retry, and it must never trigger a cancellation.
 */
export const CarCancellationSupport = ({
  cancelScenario,
  onBack,
}: CarCancellationSupportProps) => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(defaultStyles, ComponentName);

  const copy = cancelScenario ? getCancelCopy(cancelScenario) : undefined;
  const body = copy?.body?.length ? copy.body : undefined;

  return (
    <Card className={block}>
      <h1 className={cn("title")}>
        {copy?.title || t("selfServe.supportTitle")}
      </h1>

      {body ? (
        body.map((bodyText, index) => (
          <p
            key={index}
            className={cn("body")}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(bodyText) }}
          />
        ))
      ) : (
        <p className={cn("body")}>{t("selfServe.supportSubtitle")}</p>
      )}

      {copy?.disclaimer ? (
        <p className={cn("disclaimer")}>{copy.disclaimer}</p>
      ) : null}

      <CancelImportantInfo items={copy?.importantInfo} />

      <ContactSupportModal
        trigger={
          <Button
            buttonType={ButtonTypeEnum.Primary}
            className={cn("contact-btn")}
          >
            {t("selfServe.contactSupport")}
          </Button>
        }
      />

      <button type="button" className={cn("back-link")} onClick={onBack}>
        {t("selfServe.backToTripDetails")}
      </button>
    </Card>
  );
};
